// Bloco "Qualidade do áudio" do detalhe da ligação. Mostra onde o áudio piorou
// (rede do vendedor, operadora ou discador) a partir do RTCP das duas pernas,
// medido pelo ari-worker. Só aparece quando o back devolve a medição.
import { useTranslation } from "react-i18next";
import { Badge } from "@organizacaox/lai-design-system";
import { Activity } from "@organizacaox/lai-design-system/icons";
import type { CallAudioQuality, CallAudioQualityLeg } from "./api/calls.js";
import "./i18n.js";

const VERDICT_VARIANT = {
  ok: "outline",
  unknown: "outline",
  seller_network: "destructive",
  carrier: "destructive",
  server: "destructive",
} as const;

function LegLine({ label, leg }: { label: string; leg: CallAudioQualityLeg | null }) {
  const { t } = useTranslation();
  const avg = leg ? Math.max(leg.lossInPct, leg.lossOutPct) : 0;
  const worst = leg ? Math.max(leg.worstLossInPct, leg.worstLossOutPct) : 0;
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 text-sm">
      <span className={leg?.verdict === "degraded" ? "font-medium text-destructive" : ""}>
        {label}
      </span>
      <span className="text-xs text-muted-foreground tabular-nums">
        {!leg
          ? t("callDetail.audioQuality.noData")
          : [
              t("callDetail.audioQuality.loss", { avg, worst }),
              leg.jitterMs != null
                ? t("callDetail.audioQuality.jitter", { value: Math.round(leg.jitterMs) })
                : null,
            ]
              .filter(Boolean)
              .join(" · ")}
      </span>
    </div>
  );
}

export function CallAudioQualityPanel({ quality }: { quality: CallAudioQuality }) {
  const { t } = useTranslation();
  return (
    <div>
      <h2 className="mb-3 flex items-center gap-2 text-sm font-medium">
        <Activity aria-hidden className="size-4 text-muted-foreground" />
        {t("callDetail.audioQuality.title")}
      </h2>
      <div className="space-y-2 rounded-lg border border-border bg-muted/20 px-4 py-3">
        <Badge variant={VERDICT_VARIANT[quality.verdict]}>
          {t(`callDetail.audioQuality.verdict.${quality.verdict}`)}
        </Badge>
        <p className="text-xs text-muted-foreground">
          {t(`callDetail.audioQuality.hint.${quality.verdict}`)}
        </p>
        <LegLine label={t("callDetail.audioQuality.seller")} leg={quality.seller} />
        <LegLine label={t("callDetail.audioQuality.lead")} leg={quality.lead} />
      </div>
    </div>
  );
}
